import { Link } from "@tanstack/react-router";
import { Cookie } from "lucide-react";

import { openCookieSettings } from "@/components/CookieConsent";
import { Button } from "@/components/ui/button";
import { MarkdownBody } from "@/components/site/Markdown";
import type { ConsentLocale } from "@/lib/cookie-consent";
import { pathFor } from "@/lib/i18n";

const PRIVACY_PATH = "/integritetspolicy";
const COOKIE_PATH = "/cookiepolicy";

const POLICY_UI: Record<ConsentLocale, { empty: string; settings: string }> = {
  sv: { empty: "Cookiepolicyn saknar svensk text.", settings: "Ändra cookieinställningar" },
  en: { empty: "The cookie policy has no English text.", settings: "Change cookie settings" },
  es: { empty: "La política de cookies no tiene texto en español.", settings: "Cambiar preferencias de cookies" },
};

/** Visar endast exakt sparad policytext för språket. Ingen reservtext används. */
export function CookiePolicyContent({ locale, body }: { locale: ConsentLocale; body: string }) {
  const ui = POLICY_UI[locale];
  return (
    <>
      {body.trim() ? <MarkdownBody content={body} /> : <p role="status">{ui.empty}</p>}
      <div className="not-prose">
        <Button type="button" variant="outline" onClick={() => openCookieSettings()}>
          <Cookie className="h-4 w-4" aria-hidden="true" />
          {ui.settings}
        </Button>
      </div>
    </>
  );
}

const SHORT: Record<ConsentLocale, { heading: string; before: string; link: string }> = {
  sv: { heading: "Cookies", before: "Hur vi använder cookies och liknande lagring beskrivs i vår ", link: "cookiepolicy" },
  en: { heading: "Cookies", before: "How we use cookies and similar storage is described in our ", link: "cookie policy" },
  es: { heading: "Cookies", before: "El uso de cookies y almacenamiento similar se describe en nuestra ", link: "política de cookies" },
};

/** Kort hänvisning i integritetspolicyn till den separata cookiepolicyn. */
export function PolicyCookieSection({ locale }: { locale: ConsentLocale }) {
  const t = SHORT[locale];
  return (
    <>
      <h2>{t.heading}</h2>
      <p>
        {t.before}
        <Link className="text-primary link-quiet" to={pathFor(COOKIE_PATH, locale) as never}>
          {t.link}
        </Link>
        .
      </p>
    </>
  );
}
